import React, { useRef, useState } from "react";
import styled from "styled-components";
import tw from "twin.macro";
import { useTranslation } from "react-i18next";
import { SectionHeading } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import {
  Container,
  ContentWithVerticalPadding,
} from "components/misc/Layouts.js";
import { Box, IconButton, Modal, Backdrop, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { ReactComponent as CheckboxIcon } from "feather-icons/dist/icons/check-circle.svg";
import { ReactComponent as SvgDecoratorBlob1 } from "images/dot-pattern.svg";
import leaseBusinessDiagram from "images/business/lease-business.png";
import BgTitle from "components/title/BgTitle";
import LeaseBusinessMobileView from "./LeaseBusinessMobileView";
import CloseIcon from "@mui/icons-material/Close";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";

const BgWrapper = styled.div`
  ${tw`fancy-feature-mask`}
`;
const Row = tw.div`flex flex-col lg:flex-row justify-between items-center lg:items-start lg:pt-16 max-w-screen-2xl mx-auto sm:px-8`;
const Column = tw.div``;
const TextColumn = tw(
  Column
)`mr-auto lg:mr-0 max-w-lg lg:max-w-xl xl:max-w-2xl`;
const Heading2 = tw(SectionHeading)`text-left leading-snug xl:text-2xl`;
const Description = tw(SectionDescription)`mt-4 text-sm text-gray-700`;
const FeatureList = tw.ul`mt-12 leading-loose`;
const Feature = tw.li`flex items-center`;
const FeatureIcon = tw(CheckboxIcon)`w-5 h-5 text-ncc`;
const FeatureText = tw.p`ml-2 text-sm font-medium text-gray-700`;
const ContactInfo = tw.div`mt-8 rounded-lg text-left`;
const ContactText = tw.p`text-sm font-medium text-gray-700`;

const ImageColumn = tw(Column)`ml-auto lg:mr-0 relative mt-16 lg:mt-0 lg:ml-16`;
const ImageContainer = styled.div`
  ${tw`relative z-40 cursor-pointer transition-transform duration-300`}
  &:hover {
    transform: scale(1.05);
    cursor: zoom-in;
  }
`;
const Image = tw.img`
  max-w-full w-full lg:w-[550px] xl:w-[650px] rounded-lg shadow-xl relative z-20
`;
const Offsetbackground = tw.div`absolute inset-0 bg-blue-100 rounded-lg transform translate-x-4 translate-y-4 -z-10`;
const ImageDecoratorBlob = styled(SvgDecoratorBlob1)`
  ${tw`pointer-events-none z-10 absolute h-32 w-32 opacity-15 text-primary-500 fill-current`}
  right: -2rem;
  bottom: -2rem;
`;

const ZoomIconOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  opacity: 0;
  transition: opacity 0.3s ease;

  ${ImageContainer}:hover & {
    opacity: 1;
  }
`;

const ZoomHint = styled.div`
  ${tw`absolute bg-white px-3 py-1 rounded-full text-xs font-medium text-gray-700 shadow-md`}
  bottom: 0.5rem;
  right: 0.5rem;
  background-color: rgba(255, 255, 255, 0.95);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 10;

  ${ImageContainer}:hover & {
    opacity: 1;
  }
`;

const FullscreenImage = styled(Box)`
  position: relative;
  height: 90vh;
  width: 90vw;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const ImageHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: #f1f5f9;
  border-bottom: 1px solid #e5e7eb;
`;

const ImageContent = styled(Box)`
  flex: 1;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8fafc;
  padding: 24px;
`;

export default ({ isXs = false }) => {
  if (isXs) return <LeaseBusinessMobileView />;
  const myRef = useRef();
  const { t } = useTranslation();
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const openLightbox = () => setLightboxOpen(true);
  const closeLightbox = () => setLightboxOpen(false);

  return (
    <BgWrapper>
      <div ref={myRef} id="lease-business">
        <Container>
          <ContentWithVerticalPadding>
            <Row>
              <TextColumn>
                <BgTitle text={t("business.lease-of-pc.heading")} />
                <Description>
                  {t("business.lease-of-pc.description")}
                </Description>
                <FeatureList>
                  {t("business.lease-of-pc.content", {
                    returnObjects: true,
                  }).map((feature, index) => (
                    <Feature key={index}>
                      <FeatureIcon />
                      <FeatureText>{feature}</FeatureText>
                    </Feature>
                  ))}
                </FeatureList>

                {/* Contact Section */}
                <ContactInfo>
                  <Heading2>Contact</Heading2>
                  <ContactText>{t("business.lease-of-pc.contact")}</ContactText>
                </ContactInfo>
              </TextColumn>

              <ImageColumn>
                <ImageContainer onClick={openLightbox}>
                  <Image
                    src={leaseBusinessDiagram}
                    alt="Lease Business Diagram"
                  />
                  <Offsetbackground />
                  <ImageDecoratorBlob />
                  <ZoomIconOverlay>
                    <ZoomOutMapIcon style={{ fontSize: 48, color: "white" }} />
                  </ZoomIconOverlay>
                  <ZoomHint>🔍 Click to zoom</ZoomHint>
                </ImageContainer>
              </ImageColumn>
            </Row>
          </ContentWithVerticalPadding>
        </Container>
      </div>

      {/* Professional Modal for Image Zoom */}
      <Modal
        open={lightboxOpen}
        onClose={closeLightbox}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          onClick: closeLightbox,
          style: { backgroundColor: "rgba(0, 0, 0, 0.85)" },
        }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          style={{ outline: "none", maxWidth: "95vw", maxHeight: "95vh" }}
        >
          <FullscreenImage>
            <ImageHeader>
              <Typography
                variant="h6"
                component="h3"
                style={{ fontWeight: 600, color: "#1e293b" }}
              >
                {t("business.lease-of-pc.heading")}
              </Typography>
              <IconButton
                onClick={closeLightbox}
                size="small"
                style={{
                  backgroundColor: "#f1f5f9",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "rotate(90deg)";
                  e.currentTarget.style.backgroundColor = "#e2e8f0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "rotate(0deg)";
                  e.currentTarget.style.backgroundColor = "#f1f5f9";
                }}
              >
                <CloseIcon />
              </IconButton>
            </ImageHeader>
            <ImageContent>
              <img
                src={leaseBusinessDiagram}
                alt="Lease Business Diagram - Full Size"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  borderRadius: "4px",
                }}
              />
            </ImageContent>
          </FullscreenImage>
        </motion.div>
      </Modal>
    </BgWrapper>
  );
};
